import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, PowerOff, Power } from 'lucide-react';
import { useNotification } from '@iziping/ui';
import { useDeleteTable, useGetTournamentTables, useResizeTables, useToggleTable } from '../hooks/tableHooks';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import styles from './TournamentTablesPage.module.css';

interface Props {
    tournamentId: number;
}

export default function TournamentTablesPage({ tournamentId }: Props) {
    const { t } = useTranslation();
    const { notify } = useNotification();
    const { data: tables = [], isLoading } = useGetTournamentTables(tournamentId);
    const toggleTable = useToggleTable(tournamentId);
    const deleteTable = useDeleteTable(tournamentId);
    const resizeTables = useResizeTables(tournamentId);
    const [resizeCount, setResizeCount] = useState<number | ''>('');
    const [tableToDelete, setTableToDelete] = useState<number | null>(null);

    const handleToggle = async (tableId: number) => {
        try {
            await toggleTable.mutateAsync(tableId);
        } catch (err: any) {
            if (err?.error === 'table.running.match') {
                notify(t('tables.error.runningMatch'), 'error');
            } else {
                notify(t('internal.server.error'), 'error');
            }
        }
    };

    const handleDeleteConfirmed = async () => {
        if (tableToDelete === null) return;
        try {
            await deleteTable.mutateAsync(tableToDelete);
            notify(t('tables.deleted'), 'success');
        } catch (err: any) {
            if (err?.error === 'table.has.matches') {
                notify(t('tables.error.hasMatches'), 'error');
            } else {
                notify(t('internal.server.error'), 'error');
            }
        } finally {
            setTableToDelete(null);
        }
    };

    const handleResize = async () => {
        if (resizeCount === '' || resizeCount < 1) return;
        try {
            await resizeTables.mutateAsync({ count: resizeCount });
            notify(t('tables.resized'), 'success');
            setResizeCount('');
        } catch {
            notify(t('internal.server.error'), 'error');
        }
    };

    if (isLoading) return <p>{t('common.loading')}</p>;

    const activeCount = tables.filter((t) => t.usable).length;

    return (
        <>
            <div className={styles.resizeBar}>
                <span className={styles.resizeLabel}>{t('tables.resize.label')}</span>
                <input
                    type="number"
                    min={1}
                    className={styles.resizeInput}
                    value={resizeCount}
                    onChange={(e) => setResizeCount(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                    placeholder={String(tables.length)}
                />
                <button
                    className="button button--small"
                    onClick={handleResize}
                    disabled={resizeCount === '' || resizeTables.isPending}
                >
                    {t('tables.resize.apply')}
                </button>
                <span className={styles.resizeHint}>
                    {t('tables.resize.hint', { total: tables.length, active: activeCount })}
                </span>
            </div>

            <div className={styles.grid}>
                {tables.map((table) => (
                    <div
                        key={table.id}
                        className={`${styles.tableCard}${!table.usable ? ` ${styles['tableCard--disabled']}` : ''}${table.inUse ? ` ${styles['tableCard--inuse']}` : ''}`}
                    >
                        <span className={styles.tableNumber}>{t('tables.number', { number: table.number })}</span>

                        <div className={styles.badgeRow}>
                            <span
                                className={`${styles.badge} ${!table.usable ? styles['badge--disabled'] : styles['badge--active']}`}
                            >
                                {!table.usable ? t('tables.badge.disabled') : t('tables.badge.active')}
                            </span>
                        </div>

                        <div className={styles.actions}>
                            <button
                                className={styles.actionBtn}
                                title={
                                    table.inUse
                                        ? t('tables.action.disableBlocked')
                                        : !table.usable
                                          ? t('tables.action.enable')
                                          : t('tables.action.disable')
                                }
                                onClick={() => handleToggle(table.id)}
                                disabled={toggleTable.isPending || table.inUse}
                            >
                                {!table.usable ? <Power size={14} /> : <PowerOff size={14} />}
                            </button>
                            <button
                                className={`${styles.actionBtn} ${styles['actionBtn--danger']}`}
                                title={t('tables.action.delete')}
                                onClick={() => setTableToDelete(table.id)}
                                disabled={deleteTable.isPending}
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <ConfirmationModal
                isOpen={tableToDelete !== null}
                title={t('tables.delete.title')}
                message={t('tables.delete.message')}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={handleDeleteConfirmed}
                onCancel={() => setTableToDelete(null)}
            />
        </>
    );
}
